import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Building2, Plus, Edit, Trash2, Power, PowerOff, Users,
  Search, ArrowLeft, X, Mail, MapPin, CheckCircle, XCircle,
  AlertCircle, LogOut, ChevronDown
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { MODULE_HIERARCHY, ALL_MODULE_IDS } from '../../config/modulePermissions';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AMCManagement = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const [amcs, setAmcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedAMC, setSelectedAMC] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    amc_admin_name: '',
    amc_admin_email: '',
    investor_portal_domain: '',
    date_format: 'DD-MMMM-YYYY',
    enabled_modules: [...ALL_MODULE_IDS]
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);


  useEffect(() => {
    fetchAMCs();
    // Check if we should open create modal
    if (searchParams.get('action') === 'create') {
      handleCreateClick();
    }
  }, []);

  const fetchAMCs = async () => {
    try {
      const response = await axios.get(`${API_URL}/owner/amcs`);
      setAmcs(response.data);
    } catch (error) {
      console.error('Failed to fetch AMCs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    setModalMode('create');
    setFormData({
      name: '',
      address: '',
      amc_admin_name: '',
      amc_admin_email: '',
      investor_portal_domain: '',
      date_format: 'DD-MMMM-YYYY',
      enabled_modules: [...ALL_MODULE_IDS]
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEditClick = (amc) => {
    setModalMode('edit');
    setSelectedAMC(amc);
    setFormData({
      name: amc.name,
      address: amc.address || '',
      amc_admin_name: amc.amc_admin_name,
      amc_admin_email: amc.amc_admin_email,
      investor_portal_domain: amc.investor_portal_domain || '',
      date_format: amc.date_format || 'DD-MMMM-YYYY',
      enabled_modules: amc.enabled_modules || []
    });
    setFormErrors({});
    setShowModal(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'AMC name is required';
    if (!formData.amc_admin_name.trim()) errors.amc_admin_name = 'Admin name is required';
    if (!formData.amc_admin_email.trim()) {
      errors.amc_admin_email = 'Admin email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.amc_admin_email)) {
      errors.amc_admin_email = 'Invalid email format';
    }
    if (formData.enabled_modules.length === 0) {
      errors.enabled_modules = 'Select at least one module';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      if (modalMode === 'create') {
        await axios.post(`${API_URL}/owner/amcs`, formData);
      } else {
        await axios.put(`${API_URL}/owner/amcs/${selectedAMC.id}`, formData);
      }
      await fetchAMCs();
      setShowModal(false);
      alert(modalMode === 'create' ? 'AMC created successfully! Invitation email sent to admin.' : 'AMC updated successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || `Failed to ${modalMode} AMC`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (amc) => {
    if (!window.confirm(`Are you sure you want to ${amc.is_active ? 'deactivate' : 'activate'} ${amc.name}?`)) {
      return;
    }

    try {
      await axios.put(`${API_URL}/owner/amcs/${amc.id}`, {
        is_active: !amc.is_active
      });
      await fetchAMCs();
    } catch (error) {
      alert('Failed to update AMC status');
    }
  };

  const handleDelete = async (amc) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${amc.name}? This will delete ALL data associated with this AMC including users, funds, and investments. This action cannot be undone!`)) {
      return;
    }

    const confirmation = window.prompt(`Type "${amc.name}" to confirm deletion:`);
    if (confirmation !== amc.name) {
      alert('Deletion cancelled. AMC name did not match.');
      return;
    }

    try {
      await axios.delete(`${API_URL}/owner/amcs/${amc.id}`);
      await fetchAMCs();
      alert('AMC deleted successfully');
    } catch (error) {
      alert('Failed to delete AMC');
    }
  };

  const handleModuleToggle = (moduleId) => {
    setFormData(prev => {
      const isEnabled = prev.enabled_modules.includes(moduleId);
      let next;
      if (isEnabled) {
        // Disabling: also disable all submodules if it's a parent module
        const mod = MODULE_HIERARCHY.find(m => m.id === moduleId);
        const childIds = mod ? mod.submodules.map(s => s.id) : [];
        next = prev.enabled_modules.filter(m => m !== moduleId && !childIds.includes(m));
      } else {
        // Enabling: also enable parent if this is a submodule
        next = [...prev.enabled_modules, moduleId];
        const parentMod = MODULE_HIERARCHY.find(m => m.submodules.some(s => s.id === moduleId));
        if (parentMod && !next.includes(parentMod.id)) {
          next.push(parentMod.id);
        }
      }
      return { ...prev, enabled_modules: next };
    });
  };

  const filteredAMCs = amcs.filter(amc =>
    amc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    amc.amc_admin_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading AMCs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/owner/dashboard')}
                className="mr-4 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft size={24} />
              </button>
              <Building2 size={32} className="text-blue-600 mr-3" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">AMC Management</h1>
                <p className="text-xs text-gray-500">Manage Asset Management Companies</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/owner/reports')}
                className="flex items-center px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Building2 size={18} className="mr-2" />
                View Reports
              </button>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-blue-600">Platform Owner</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut size={18} className="mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">All AMCs</h2>
            <p className="text-gray-600 mt-1">{amcs.length} total AMCs</p>
          </div>
          <button
            onClick={handleCreateClick}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={20} className="mr-2" />
            Create AMC
          </button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search AMCs by name or admin email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* AMC List */}
        <div className="grid grid-cols-1 gap-4">
          {filteredAMCs.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <Building2 size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">No AMCs found</p>
            </div>
          ) : (
            filteredAMCs.map((amc) => (
              <div
                key={amc.id}
                className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 mr-3">{amc.name}</h3>
                      {amc.is_active ? (
                        <span className="flex items-center px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                          <CheckCircle size={14} className="mr-1" />
                          Active
                        </span>
                      ) : (
                        <span className="flex items-center px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">
                          <XCircle size={14} className="mr-1" />
                          Inactive
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div className="flex items-start text-sm text-gray-600">
                        <MapPin size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                        <span>{amc.address || 'No address provided'}</span>
                      </div>
                      <div className="flex items-start text-sm text-gray-600">
                        <Users size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-gray-900">{amc.amc_admin_name}</p>
                          <p className="flex items-center">
                            <Mail size={14} className="mr-1" />
                            {amc.amc_admin_email}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4">
                      <p className="text-xs text-gray-500 mb-2">Enabled Modules:</p>
                      <div className="flex flex-wrap gap-2">
                        {MODULE_HIERARCHY.filter(mod => amc.enabled_modules?.includes(mod.id)).map((mod) => {
                          const enabledSubs = mod.submodules.filter(s => amc.enabled_modules?.includes(s.id));
                          return (
                            <span key={mod.id} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded" title={enabledSubs.length > 0 ? enabledSubs.map(s => s.label).join(', ') : undefined}>
                              {mod.label}{enabledSubs.length > 0 && mod.submodules.length > 0 ? ` (${enabledSubs.length}/${mod.submodules.length})` : ''}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col space-y-2 ml-4">
                    <button
                      onClick={() => navigate(`/owner/amcs/${amc.id}`)}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                    >
                      View Details
                    </button>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEditClick(amc)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit AMC"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleToggleActive(amc)}
                        className={`p-2 ${amc.is_active ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'} rounded-lg transition-colors`}
                        title={amc.is_active ? 'Deactivate AMC' : 'Activate AMC'}
                      >
                        {amc.is_active ? <PowerOff size={18} /> : <Power size={18} />}
                      </button>
                      <button
                        onClick={() => handleDelete(amc)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete AMC"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-6 py-4 flex items-center justify-between rounded-t-xl">
              <h3 className="text-xl font-semibold">
                {modalMode === 'create' ? 'Create New AMC' : 'Edit AMC'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-1"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {/* AMC Name */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  AMC Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full px-4 py-2 border ${formErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Enter AMC name"
                />
                {formErrors.name && <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>}
              </div>

              {/* Address */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  rows="2"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter address"
                />
              </div>

              {/* Investor Portal Domain — the public origin used in WhatsApp
                  short-links and the Generated Notices UI for this AMC. When
                  empty we fall back to the platform default. */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Investor Portal Domain
                  <span className="ml-1 text-xs font-normal text-gray-500">
                    · used in WhatsApp short-links for this AMC&apos;s investors
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.investor_portal_domain}
                  onChange={(e) => setFormData({ ...formData, investor_portal_domain: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. https://carpediem-capital.com (leave blank to use platform default)"
                  data-testid="amc-investor-portal-domain-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Bare hostnames (e.g. <code className="bg-gray-100 px-1 rounded">carpediem-capital.com</code>) are auto-upgraded to <code className="bg-gray-100 px-1 rounded">https://</code>. Leave empty to inherit <code className="bg-gray-100 px-1 rounded">PUBLIC_APP_URL</code> from the platform.
                </p>
              </div>

              {/* Date Format — display-only preference used by the frontend
                  formatter hook so every ledger, drawdown, transfer and
                  contact interaction renders in the AMC's local convention. */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date Format
                  <span className="ml-1 text-xs font-normal text-gray-500">
                    · how calendar dates render across the AMC UI
                  </span>
                </label>
                <select
                  value={formData.date_format}
                  onChange={(e) => setFormData({ ...formData, date_format: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  data-testid="amc-date-format-select"
                >
                  <option value="DD-MMMM-YYYY">DD-Month-YYYY (17-June-2026)</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY (17/06/2026)</option>
                  <option value="MMM DD, YYYY">Mon DD, YYYY (Jun 17, 2026)</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD (2026-06-17)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Affects display only — stored dates remain ISO-safe. Users must log in again for the change to take effect.
                </p>
              </div>

              {/* Admin Name */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  AMC Admin Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.amc_admin_name}
                  onChange={(e) => setFormData({ ...formData, amc_admin_name: e.target.value })}
                  className={`w-full px-4 py-2 border ${formErrors.amc_admin_name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Enter admin name"
                  disabled={modalMode === 'edit'}
                />
                {formErrors.amc_admin_name && <p className="text-red-500 text-sm mt-1">{formErrors.amc_admin_name}</p>}
                {modalMode === 'edit' && (
                  <p className="text-xs text-gray-500 mt-1">Admin name cannot be changed after creation</p>
                )}
              </div>

              {/* Admin Email */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  AMC Admin Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.amc_admin_email}
                  onChange={(e) => setFormData({ ...formData, amc_admin_email: e.target.value })}
                  className={`w-full px-4 py-2 border ${formErrors.amc_admin_email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="admin@example.com"
                  disabled={modalMode === 'edit'}
                />
                {formErrors.amc_admin_email && <p className="text-red-500 text-sm mt-1">{formErrors.amc_admin_email}</p>}
                {modalMode === 'edit' && (
                  <p className="text-xs text-gray-500 mt-1">Admin email cannot be changed after creation</p>
                )}
              </div>

              {/* Enabled Modules */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enabled Modules & Submodules <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3">
                  {MODULE_HIERARCHY.map((mod) => {
                    const isModEnabled = formData.enabled_modules.includes(mod.id);
                    const hasSubs = mod.submodules.length > 0;
                    const enabledSubCount = mod.submodules.filter(s => formData.enabled_modules.includes(s.id)).length;
                    const allSubsEnabled = enabledSubCount === mod.submodules.length;
                    return (
                      <div key={mod.id} className={`border rounded-lg overflow-hidden ${isModEnabled ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200'}`}>
                        <label className="flex items-center px-4 py-3 cursor-pointer hover:bg-gray-50" data-testid={`amc-toggle-${mod.id}`}>
                          <input
                            type="checkbox"
                            checked={isModEnabled}
                            onChange={() => handleModuleToggle(mod.id)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="ml-3 text-sm font-semibold text-gray-800">{mod.label}</span>
                          {hasSubs && isModEnabled && (
                            <span className="ml-auto text-xs text-gray-500">{enabledSubCount}/{mod.submodules.length} submodules</span>
                          )}
                        </label>
                        {hasSubs && isModEnabled && (
                          <div className="border-t border-gray-200 bg-white px-4 py-2 grid grid-cols-2 gap-2">
                            {mod.submodules.map(sub => (
                              <label key={sub.id} className="flex items-center py-1.5 cursor-pointer" data-testid={`amc-toggle-${sub.id}`}>
                                <input
                                  type="checkbox"
                                  checked={formData.enabled_modules.includes(sub.id)}
                                  onChange={() => handleModuleToggle(sub.id)}
                                  className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="ml-2 text-sm text-gray-700">{sub.label}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {formErrors.enabled_modules && <p className="text-red-500 text-sm mt-1">{formErrors.enabled_modules}</p>}
              </div>

              {modalMode === 'create' && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start">
                    <AlertCircle size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Note:</p>
                      <p>An invitation email will be sent to the admin email address with login credentials and a verification link.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : (modalMode === 'create' ? 'Create AMC' : 'Update AMC')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AMCManagement;
